/**
 * MoneySelect — money-value dropdown with a "Custom Value" affordance.
 *
 * Use when a field has a canonical set of preset dollar values (e.g.
 * Retention) but the user occasionally needs to enter a value that
 * isn't in the list. The dropdown shows:
 *
 *   • A list of preset items.
 *   • A "Custom Value" pill button at the bottom.
 *
 * Clicking the pill flips the bottom area into an inline number input
 * focused for typing. Entering a value and pressing Enter:
 *   1. Adds the value to the list of options as a new item.
 *   2. Selects it.
 *   3. Closes the popover.
 *
 * The component is uncontrolled w.r.t. the input draft (managed
 * locally) but controlled w.r.t. the selected value (caller passes
 * `value` + `onChange`). It accepts plain numeric strings as values —
 * formatting to "$NN,NNN" is done at render time.
 *
 * With `isClearable`, clicking the selected option again clears the
 * value back to "" (empty state shows the placeholder).
 */
import { useState } from "react";
import type { Key } from "react-aria-components";
import {
    Autocomplete as AriaAutocomplete,
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    ListBox as AriaListBox,
    Popover as AriaPopover,
} from "react-aria-components";
import { ChevronDown, Plus } from "@untitledui/icons";
import { Select, cx } from "@tricura/atlas-ui";

interface MoneySelectProps {
    /** Currently selected value (numeric string, e.g. "50000"). */
    value: string;
    /** Fired when the user picks a preset OR confirms a custom entry. */
    onChange: (next: string) => void;
    /** Preset values shown at the top of the list. */
    presets: number[];
    /** Visible label for the trigger when nothing is selected. */
    placeholder?: string;
    isInvalid?: boolean;
    /** Clicking the selected option again resets the value to "". */
    isClearable?: boolean;
    /** ARIA label / data-autotest hint for testing. */
    "aria-label"?: string;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

function formatMoney(raw: string | number): string {
    const n = typeof raw === "string" ? Number(raw.replace(/[^\d.-]/g, "")) : raw;
    if (!Number.isFinite(n)) return "";
    return `$${n.toLocaleString("en-US")}`;
}

function parseMoney(input: string): number | null {
    const cleaned = input.replace(/[^\d.-]/g, "");
    if (cleaned === "") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

export function MoneySelect({ value, onChange, presets, placeholder = "Select", isInvalid, isClearable, "aria-label": ariaLabel, "data-autotest": dataAutotest }: MoneySelectProps) {
    /** Custom values the user has added in this session — kept in
     *  component state so the option appears alongside presets. */
    const [customs, setCustoms] = useState<number[]>(() => {
        const n = parseMoney(value);
        return n != null && !presets.includes(n) ? [n] : [];
    });
    /** Whether the bottom area is currently in "type a custom value" mode. */
    const [draftOpen, setDraftOpen] = useState(false);
    const [draft, setDraft] = useState("");

    const allValues = [...presets, ...customs.filter((c) => !presets.includes(c))];
    const selectedNumber = parseMoney(value);
    const items = allValues.map((n) => ({ id: String(n), label: formatMoney(n) }));

    const commit = (raw: string, close: () => void) => {
        const n = parseMoney(raw);
        if (n == null) {
            setDraft("");
            return;
        }
        if (!presets.includes(n) && !customs.includes(n)) {
            setCustoms((prev) => [...prev, n]);
        }
        onChange(String(n));
        setDraft("");
        setDraftOpen(false);
        close();
    };

    return (
        <AriaDialogTrigger>
            <AriaButton
                data-autotest={dataAutotest}
                className={(state) =>
                    cx(
                        "bg-primary shadow-xs ring-primary outline-hidden relative flex h-9 w-full cursor-pointer items-center rounded-lg px-3 text-sm ring-1 ring-inset transition duration-100 ease-linear",
                        (state.isFocusVisible || state.isPressed) && "ring-brand ring-2",
                        state.isDisabled && "cursor-not-allowed opacity-50",
                        isInvalid && "ring-error_subtle",
                    )
                }
            >
                <span className={cx("flex w-full items-center truncate text-left")}>
                    {selectedNumber != null ? (
                        <span className="text-primary truncate font-medium">{formatMoney(selectedNumber)}</span>
                    ) : (
                        <span className="text-placeholder">{placeholder}</span>
                    )}
                    <ChevronDown aria-hidden="true" className="size-4 text-fg-quaternary ml-auto shrink-0 stroke-[2.25px]" />
                </span>
            </AriaButton>

            <AriaPopover
                placement="bottom start"
                offset={4}
                className="w-(--trigger-width) bg-primary ring-secondary_alt outline-hidden overflow-hidden rounded-lg shadow-lg ring-1"
            >
                <AriaDialog className="outline-hidden">
                    {({ close }) => (
                        <>
                            <AriaAutocomplete>
                                <AriaListBox
                                    aria-label={ariaLabel || "Money value"}
                                    items={items}
                                    selectionMode="single"
                                    selectedKeys={selectedNumber != null ? new Set<Key>([String(selectedNumber)]) : new Set<Key>()}
                                    onSelectionChange={(keys) => {
                                        if (keys === "all") return;
                                        const k = keys.size > 0 ? Array.from(keys)[0] : null;
                                        if (k != null) {
                                            onChange(String(k));
                                            close();
                                        } else if (isClearable) {
                                            // Re-clicking the selected option deselects it.
                                            onChange("");
                                            close();
                                        }
                                    }}
                                    className="outline-hidden max-h-72 overflow-y-auto py-1"
                                >
                                    {(item) => (
                                        <Select.Item id={item.id} data-autotest={dataAutotest ? `${dataAutotest}-option-${item.label}` : undefined}>
                                            {item.label}
                                        </Select.Item>
                                    )}
                                </AriaListBox>
                            </AriaAutocomplete>

                            <div className="border-secondary border-t p-3">
                                {draftOpen ? (
                                    <input
                                        type="text"
                                        autoFocus
                                        inputMode="decimal"
                                        data-autotest={dataAutotest ? `${dataAutotest}-custom-input` : undefined}
                                        placeholder="Enter Custom Value..."
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                commit(draft, close);
                                            } else if (e.key === "Escape") {
                                                e.preventDefault();
                                                setDraftOpen(false);
                                                setDraft("");
                                            }
                                        }}
                                        onBlur={() => {
                                            // Commit on blur if the user typed something — else
                                            // collapse back to the button without saving.
                                            if (draft.trim() !== "") commit(draft, close);
                                            else setDraftOpen(false);
                                        }}
                                        className="bg-primary text-primary ring-brand placeholder:text-placeholder w-full rounded-lg px-3 py-2 text-sm outline-none ring-2"
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        data-autotest={dataAutotest ? `${dataAutotest}-custom-button` : undefined}
                                        onClick={() => setDraftOpen(true)}
                                        className="bg-primary text-primary ring-secondary hover:bg-secondary inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold ring-1 transition duration-100 ease-linear"
                                    >
                                        <Plus aria-hidden="true" className="size-4 stroke-[2.25px]" />
                                        Custom Value
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </AriaDialog>
            </AriaPopover>
        </AriaDialogTrigger>
    );
}
